import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  User as UserIcon,
  Phone,
  Mail,
  Calendar,
  Briefcase,
  MapPin,
  ClipboardList,
  Heart,
  Droplets,
} from 'lucide-react';
import type { AdminMember, Community, BusinessAddress } from '@/types';
import { format } from 'date-fns';
import { formatDisplayPhone } from '@/lib/phone-utils';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { getFormValueFromStoredOnboarding } from '@/lib/onboarding-answers';

interface MemberDetailsDialogProps {
  member: AdminMember | null;
  community: Community;
  onClose: () => void;
}

const formatDate = (date: string | null | undefined) => {
  if (!date) return 'Not set';
  try {
    return format(new Date(date), 'PPP');
  } catch {
    return 'Invalid date';
  }
};

const formatAddress = (address: string | BusinessAddress | null | undefined) => {
  if (!address) return 'Not set';
  if (typeof address === 'string') return address;

  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.landmark,
    address.district,
    address.state,
    address.pinCode,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : 'Not set';
};

export function MemberDetailsDialog({ member, community, onClose }: MemberDetailsDialogProps) {
  if (!member) return null;

  const onboardingConfig = community.onboardingForm;
  const onboardingAnswers = member.metadata?.onboardingAnswers || {};

  return (
    <Dialog open={!!member} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] h-[90vh] flex flex-col p-0 overflow-hidden bg-background border-border shadow-2xl rounded-3xl">
        <DialogHeader className="p-8 pb-4 relative overflow-hidden border-b border-border/10">
          {/* Header Background Accent */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-background opacity-80" />

          <div className="flex items-center gap-6 relative z-10">
            <Avatar className="h-24 w-24 border-2 border-primary/20 shadow-xl ring-4 ring-background">
              <AvatarImage src={member.image} className="object-cover" />
              <AvatarFallback className="bg-gradient-premium text-white text-3xl font-bold">
                {member.name[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-3xl font-extrabold tracking-tight text-foreground truncate">
                {member.name}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-2 text-muted-foreground font-medium text-base">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest border border-primary/20">
                  Member
                </span>
                <span className="text-muted-foreground/60">•</span>
                <span className="text-sm">Joined {formatDate(member.joinDate)}</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-8 pb-8">
          <div className="space-y-10 py-6">
            {/* Personal Details */}
            <section className="space-y-5">
              <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-primary/80 flex items-center gap-2 px-1">
                <UserIcon className="h-3.5 w-3.5" /> Personal Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-secondary/30 dark:bg-card/40 p-7 rounded-[2.5rem] border border-border/50 shadow-inner">
                <InfoItem
                  icon={Mail}
                  label="Email"
                  value={member.email}
                  isLink={`mailto:${member.email}`}
                />
                <InfoItem
                  icon={Phone}
                  label="Phone"
                  value={formatDisplayPhone(member.phone)}
                  isLink={`tel:${formatDisplayPhone(member.phone)}`}
                />
                <InfoItem icon={Calendar} label="Date of Birth" value={formatDate(member.dob)} />
                <InfoItem
                  icon={Heart}
                  label="Anniversary"
                  value={formatDate(member.anniversaryDate)}
                />
                <InfoItem icon={UserIcon} label="Gender" value={member.gender || 'Not specified'} />
                <InfoItem
                  icon={Droplets}
                  label="Blood Group"
                  value={member.bloodGroup || 'Not specified'}
                />
              </div>
            </section>

            <Separator className="opacity-10 translate-x-1" />

            {/* Business Details */}
            {member.businessName && (
              <>
                <section className="space-y-5">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-primary/80 flex items-center gap-2 px-1">
                    <Briefcase className="h-3.5 w-3.5" /> Business Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-secondary/30 dark:bg-card/40 p-7 rounded-[2.5rem] border border-border/50 shadow-inner">
                    <div className="md:col-span-2">
                      <InfoItem label="Company Name" value={member.businessName} />
                    </div>
                    <InfoItem
                      label="Category"
                      value={
                        formatBusinessCategoryLabel(
                          member.businessCategory,
                          member.subCategory,
                          member.selfDefinedCategory
                        ) || 'Not set'
                      }
                    />
                    <InfoItem
                      label="Business Phone"
                      value={member.businessPhone || 'Not set'}
                      isLink={member.businessPhone ? `tel:${member.businessPhone}` : undefined}
                    />
                    <div className="md:col-span-2">
                      <InfoItem
                        icon={MapPin}
                        label="Address"
                        value={formatAddress(member.businessAddress)}
                      />
                    </div>
                  </div>
                </section>
                <Separator className="opacity-10 translate-x-1" />
              </>
            )}

            {/* Additional details (member answers) */}
            {onboardingConfig && onboardingConfig.steps && (
              <section className="space-y-6">
                <h4 className="text-[10px] font-black uppercase tracking-[0.25em] text-primary/80 flex items-center gap-2 px-1">
                  <ClipboardList className="h-3.5 w-3.5" /> Additional details
                </h4>
                <div className="space-y-8">
                  {onboardingConfig.steps.map((step) => (
                    <div key={step.id} className="space-y-4">
                      <h5 className="text-[11px] font-black text-muted-foreground/80 uppercase tracking-[0.2em] ml-2 flex items-center gap-3">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary shadow-sm shadow-primary/40" />
                        {step.title}
                      </h5>
                      <div className="grid grid-cols-1 gap-4">
                        {step.fields.map((field) => (
                          <div
                            key={field.id}
                            className="bg-card dark:bg-background/40 p-6 rounded-[2rem] border border-border shadow-sm hover:shadow-xl hover:border-primary/30 transition-all duration-500 group"
                          >
                            <p className="text-[10px] text-muted-foreground uppercase font-black tracking-widest mb-3 group-hover:text-primary transition-colors">
                              {field.label}
                            </p>
                            <div className="text-sm font-bold text-foreground leading-relaxed">
                              {renderAnswer(
                                getFormValueFromStoredOnboarding(onboardingAnswers[field.id])
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function InfoItem({
  icon: Icon,
  label,
  value,
  isLink,
}: {
  icon?: React.ElementType;
  label: string;
  value: string;
  isLink?: string;
}) {
  return (
    <div className="flex items-start gap-4 group">
      {Icon && (
        <div className="p-3 rounded-2xl bg-background dark:bg-card group-hover:bg-primary/5 transition-all duration-300 shrink-0 shadow-sm border border-border group-hover:border-primary/20">
          <Icon className="h-4 w-4 text-primary/70 group-hover:text-primary transition-colors" />
        </div>
      )}
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] text-muted-foreground uppercase font-black tracking-[0.2em] mb-1">
          {label}
        </span>
        {isLink ? (
          <a
            href={isLink}
            className="text-sm font-extrabold text-foreground hover:text-primary transition-all truncate decoration-primary/30 underline-offset-4 hover:underline decoration-2"
          >
            {value}
          </a>
        ) : (
          <span className="text-sm font-extrabold text-foreground truncate">{value}</span>
        )}
      </div>
    </div>
  );
}

function renderAnswer(
  answer: string | string[] | boolean | number | null | undefined
): React.ReactNode {
  if (answer === null || answer === undefined) return 'No answer provided';

  const isImage = (str: string) => {
    if (typeof str !== 'string') return false;
    const isUrl = str.startsWith('http://') || str.startsWith('https://');
    if (!isUrl) return false;
    return (
      /\.(jpeg|jpg|gif|png|webp|avif|svg)(\?.*)?$/i.test(str) ||
      str.includes('firebasestorage.googleapis.com') ||
      str.includes('utfs.io') ||
      str.includes('res.cloudinary.com')
    );
  };

  if (Array.isArray(answer)) {
    const hasImages = answer.some((item) => typeof item === 'string' && isImage(item));
    if (hasImages) {
      return (
        <div className="flex flex-wrap gap-4 mt-3">
          {answer.map((item, i) =>
            typeof item === 'string' && isImage(item) ? (
              <a
                key={i}
                href={item}
                target="_blank"
                rel="noopener noreferrer"
                className="block relative group cursor-zoom-in"
              >
                <img
                  src={item}
                  alt={`Response Image ${i + 1}`}
                  className="max-w-[14rem] w-full h-auto rounded-xl object-cover border border-border/50 shadow-sm group-hover:shadow-lg group-hover:ring-2 group-hover:ring-primary/40 transition-all duration-300"
                  loading="lazy"
                />
              </a>
            ) : (
              <span key={i} className="inline-block mt-1">
                {String(item)}
                {i < answer.length - 1 ? ', ' : ''}
              </span>
            )
          )}
        </div>
      );
    }
    return answer.join(', ');
  }

  if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';

  if (typeof answer === 'string' && isImage(answer)) {
    return (
      <a
        href={answer}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block mt-3 relative group cursor-zoom-in"
      >
        <img
          src={answer}
          alt="Response Image"
          className="max-w-[18rem] w-full h-auto rounded-xl object-cover border border-border/50 shadow-sm group-hover:shadow-lg group-hover:ring-2 group-hover:ring-primary/40 transition-all duration-300"
          loading="lazy"
        />
      </a>
    );
  }

  if (typeof answer === 'string') {
    const date = formatDate(answer);
    if (date !== 'Invalid date') return date;
  }

  return String(answer);
}
